import { useState } from "react";
import ProfileSection from "./ProfileSection";
import OrdersSection from "./OrdersSection";
import AddressSection from "./AddressSection";
import NotificationSection from "./NotificationSection";
import { useUserAuth } from "../../context/UserAuthContext";
import { useCart } from "../../context/CartContext";
import {
  Bell,
  LogOut,
  MapPin,
  Menu,
  Package,
  UserRound,
  X,
} from "lucide-react";

const tabIcon = {
  profile: UserRound,
  orders: Package,
  address: MapPin,
  notifications: Bell,
};

const UserDashboard = () => {
  const { logout, user } = useUserAuth();
  const { clearCart } = useCart();
  const [activeTab, setActiveTab] = useState("profile");
  const [menuOpen, setMenuOpen] = useState(false);

  const tabs = [
    { label: "Profile", value: "profile" },
    { label: "Orders", value: "orders" },
    { label: "Addresses", value: "address" },
    { label: "Notifications", value: "notifications" },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case "profile":
        return <ProfileSection />;
      case "orders":
        return <OrdersSection />;
      case "address":
        return <AddressSection />;
      case "notifications":
        return <NotificationSection />;
      default:
        return <ProfileSection />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/90 via-amber-50/20 to-stone-100 px-4 py-8 sm:px-5 sm:py-10">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl border border-stone-200/90 bg-white shadow-xl shadow-stone-200/40 ring-1 ring-black/[0.03]">
        {/* Mobile header */}
        <div className="flex items-center justify-between border-b border-stone-100 bg-white px-4 py-3.5 sm:px-5 lg:hidden">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-orange-600/90">Account</p>
            <h1 className="text-lg font-bold tracking-tight text-stone-900">Dashboard</h1>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-stone-200 bg-stone-50 text-stone-700 transition hover:bg-stone-100"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <X className="h-5 w-5" strokeWidth={2.25} /> : <Menu className="h-5 w-5" strokeWidth={2.25} />}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Sidebar */}
          <aside
            className={`${
              menuOpen ? "block" : "hidden"
            } border-b border-stone-100 bg-gradient-to-b from-stone-50/80 to-white lg:block lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r lg:border-stone-100`}
          >
            <nav className="p-3 lg:sticky lg:top-0 lg:py-6" aria-label="Account sections">
              {user?.name || user?.email ? (
                <div className="mb-4 hidden rounded-xl border border-stone-200/80 bg-white px-3 py-3 shadow-sm lg:block">
                  <p className="truncate text-sm font-bold text-stone-900">{user.name || "Account"}</p>
                  {user.email ? (
                    <p className="truncate text-xs text-stone-500">{user.email}</p>
                  ) : null}
                </div>
              ) : null}
              <p className="mb-2 hidden px-3 text-[10px] font-bold uppercase tracking-widest text-stone-400 lg:block">
                Menu
              </p>
              <ul className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tabIcon[tab.value] || UserRound;
                  const active = activeTab === tab.value;
                  return (
                    <li key={tab.value}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab(tab.value);
                          setMenuOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition lg:py-2.5 ${
                          active
                            ? "bg-orange-50 text-orange-900 shadow-sm ring-1 ring-orange-200/60"
                            : "text-stone-600 hover:bg-white hover:text-stone-900"
                        }`}
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                            active ? "bg-orange-500 text-white shadow-md shadow-orange-300/40" : "bg-stone-100 text-stone-500"
                          }`}
                        >
                          <Icon className="h-4 w-4" strokeWidth={2.25} />
                        </span>
                        {tab.label}
                        {active ? (
                          <span className="ml-auto hidden h-1.5 w-1.5 rounded-full bg-orange-500 lg:block" aria-hidden />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-4 border-t border-stone-100 p-3 pt-4">
                <button
                  type="button"
                  onClick={async () => {
                    await clearCart();
                    await logout();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-stone-200 bg-white px-4 py-2.5 text-sm font-bold text-stone-700 shadow-sm transition hover:border-stone-300 hover:bg-stone-50"
                >
                  <LogOut className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                  Sign out
                </button>
              </div>
            </nav>
          </aside>

          {/* Main */}
          <div className="min-h-[min(70vh,32rem)] min-w-0 flex-1 bg-stone-50/30 lg:min-h-[28rem]">
            <div className="border-b border-stone-100 px-5 py-5 sm:px-8 sm:py-6 lg:hidden">
              <p className="text-xs font-semibold text-stone-500">
                {tabs.find((t) => t.value === activeTab)?.label}
              </p>
            </div>
            <div className="px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-10">{renderTab()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
